import React, { useCallback, useMemo, useState } from "react";
import { z } from "zod";
import { useActiveOrganization } from "@hooks/useActiveOrganization";
import {
    usePatchOrganizationClientMutation,
    useUpdateClientEntityMutation,
} from "@api/organizationClient.api";
import { OrganizationEntityPriority } from "@common/types/organizationEntity";
import { EntityCloseStage } from "@common/types/entity";
import { OrganizationServicesOffered } from "@common/types/organization";
import { ChildrenProps } from "../../../../types";
import { useClientBulkSelection } from "../ClientBulkSelection/useClientBulkSelection";
import {
    clientBulkActionsContext,
    ClientBulkActionPayload,
} from "./clientBulkActionsContext";

// Schema to validate entity rows have workspaceId
const entityRowSchema = z.object({
    workspaceId: z.string(),
    id: z.string(),
});

export const ClientBulkActionsProvider: React.FC<ChildrenProps> = ({
    children,
}) => {
    const [busy, setBusy] = useState(false);
    const bulkSelection = useClientBulkSelection();
    const activeOrganization = useActiveOrganization();
    const patchClientMutation = usePatchOrganizationClientMutation();
    const updateEntityMutation = useUpdateClientEntityMutation();

    const runBulkAction = useCallback(
        async (payload: ClientBulkActionPayload) => {
            if (!activeOrganization) {
                return;
            }

            const applyBulkAction = async () => {
                setBusy(true);

                const { action, value } = payload;

                if (action === "note") {
                    // Update notes for selected items
                    const noteValue = (value as string) ?? null;
                    const updatePromises = bulkSelection.selected.map(
                        async (item) => {
                            if (item.type === "workspace") {
                                return await patchClientMutation.mutateAsync({
                                    organizationId: activeOrganization.id,
                                    clientId: item.id,
                                    payload: { note: noteValue },
                                });
                            } else {
                                // Validate that the item has the required entity fields
                                const parseResult =
                                    entityRowSchema.safeParse(item);

                                if (!parseResult.success) {
                                    return await Promise.resolve();
                                }

                                const { workspaceId, id } = parseResult.data;

                                return await updateEntityMutation.mutateAsync({
                                    organizationId: activeOrganization.id,
                                    clientId: workspaceId,
                                    entityId: Number(id),
                                    payload: { note: noteValue },
                                });
                            }
                        },
                    );

                    await Promise.all(updatePromises);

                    // Clear selection after successful update
                    bulkSelection.clearSelection();
                }

                if (action === "priority") {
                    // Priority can only be set on entities, not workspaces
                    const entityItems = bulkSelection.selected.filter(
                        (item) => item.type === "entity",
                    );

                    const priorityValue = value as
                        | OrganizationEntityPriority
                        | undefined;

                    const updatePromises = entityItems.map(async (item) => {
                        const parseResult = entityRowSchema.safeParse(item);

                        if (!parseResult.success) {
                            return await Promise.resolve();
                        }

                        const { workspaceId, id } = parseResult.data;

                        return await updateEntityMutation.mutateAsync({
                            organizationId: activeOrganization.id,
                            clientId: workspaceId,
                            entityId: Number(id),
                            payload: { priority: priorityValue ?? null },
                        });
                    });

                    await Promise.all(updatePromises);

                    // Clear selection after successful update
                    bulkSelection.clearSelection();
                }

                if (action === "closeDate") {
                    // Close date can only be set on entities, not workspaces
                    const entityItems = bulkSelection.selected.filter(
                        (item) => item.type === "entity",
                    );

                    const closeDateValue = value as string | null | undefined;

                    const updatePromises = entityItems.map(async (item) => {
                        const parseResult = entityRowSchema.safeParse(item);

                        if (!parseResult.success) {
                            return await Promise.resolve();
                        }

                        const { workspaceId, id } = parseResult.data;

                        return await updateEntityMutation.mutateAsync({
                            organizationId: activeOrganization.id,
                            clientId: workspaceId,
                            entityId: Number(id),
                            payload: { closeDate: closeDateValue ?? null },
                        });
                    });

                    await Promise.all(updatePromises);

                    // Clear selection after successful update
                    bulkSelection.clearSelection();
                }

                if (action === "dueDate") {
                    // Due date can only be set on entities, not workspaces
                    const entityItems = bulkSelection.selected.filter(
                        (item) => item.type === "entity",
                    );

                    const dueDateValue = value as string | null | undefined;

                    const updatePromises = entityItems.map(async (item) => {
                        const parseResult = entityRowSchema.safeParse(item);

                        if (!parseResult.success) {
                            return await Promise.resolve();
                        }

                        const { workspaceId, id } = parseResult.data;

                        return await updateEntityMutation.mutateAsync({
                            organizationId: activeOrganization.id,
                            clientId: workspaceId,
                            entityId: Number(id),
                            payload: { dueDate: dueDateValue ?? null },
                        });
                    });

                    await Promise.all(updatePromises);

                    // Clear selection after successful update
                    bulkSelection.clearSelection();
                }

                if (action === "services") {
                    const servicesValue =
                        value as OrganizationServicesOffered[];

                    const updatePromises = bulkSelection.selected.map(
                        async (item) => {
                            if (item.type === "workspace") {
                                return await patchClientMutation.mutateAsync({
                                    organizationId: activeOrganization.id,
                                    clientId: item.id,
                                    payload: { servicesOffered: servicesValue },
                                });
                            } else {
                                const parseResult =
                                    entityRowSchema.safeParse(item);

                                if (!parseResult.success) {
                                    return await Promise.resolve();
                                }

                                const { workspaceId, id } = parseResult.data;

                                return await updateEntityMutation.mutateAsync({
                                    organizationId: activeOrganization.id,
                                    clientId: workspaceId,
                                    entityId: Number(id),
                                    payload: { services: servicesValue },
                                });
                            }
                        },
                    );

                    await Promise.all(updatePromises);

                    // Clear selection after successful update
                    bulkSelection.clearSelection();
                }

                if (action === "stage") {
                    // Stage can only be set on entities, not workspaces
                    const entityItems = bulkSelection.selected.filter(
                        (item) => item.type === "entity",
                    );

                    const stageValue = value as EntityCloseStage | undefined;

                    const updatePromises = entityItems.map(async (item) => {
                        const parseResult = entityRowSchema.safeParse(item);

                        if (!parseResult.success) {
                            return await Promise.resolve();
                        }

                        const { workspaceId, id } = parseResult.data;

                        return await updateEntityMutation.mutateAsync({
                            organizationId: activeOrganization.id,
                            clientId: workspaceId,
                            entityId: Number(id),
                            payload: { closeStage: stageValue ?? null },
                        });
                    });

                    await Promise.all(updatePromises);

                    // Clear selection after successful update
                    bulkSelection.clearSelection();
                }

                if (action === "assignee") {
                    // Assignee can only be set on workspaces, not entities
                    const workspaceItems = bulkSelection.selected.filter(
                        (item) => item.type === "workspace",
                    );

                    const assigneeValue = value as number | null | undefined;

                    const updatePromises = workspaceItems.map((item) =>
                        patchClientMutation.mutateAsync({
                            organizationId: activeOrganization.id,
                            clientId: item.id,
                            payload: { assigneeId: assigneeValue ?? null },
                        }),
                    );

                    await Promise.all(updatePromises);

                    // Clear selection after successful update
                    bulkSelection.clearSelection();
                }
            };
            await applyBulkAction().finally(() => {
                setBusy(false);
            });
        },
        [
            activeOrganization,
            bulkSelection,
            patchClientMutation,
            updateEntityMutation,
        ],
    );

    const value = useMemo(
        () => ({
            run: runBulkAction,
            busy,
        }),
        [busy, runBulkAction],
    );

    return (
        <clientBulkActionsContext.Provider value={value}>
            {children}
        </clientBulkActionsContext.Provider>
    );
};
